import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthMirrorService } from '@core/services/core/auth/auth-mirror.service';
import { Platform } from '@ionic/angular';
import { isServeMode } from '@utils/utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first, map, share, tap } from 'rxjs/operators';

export type AccessType = 'particular' | 'business';
@Injectable({
  providedIn: 'root'
})
export class ContextService {
  // Use it to enable unbound pure functions on streams like proccess
  static isCordova;

  isFirstPage$ = new BehaviorSubject(true);
  previousPage = '';
  currentPage = '';
  private _accessType = new BehaviorSubject<AccessType>('particular');
  private _onPageChange$;

  constructor(
    private platform: Platform,
    private router: Router,
    private auth: AuthMirrorService
  ) {
    this.onPageChange$()
      .pipe(first())
      .subscribe(() => this.isFirstPage$.next(false));
    ContextService.isCordova = this.platform.is('cordova');
  }

  /**
   * @description Indicator whether the app is running on a browser or on an hybrid app
   * IMPORTANT: This should only be used for View purposes, not for cordova plugins
   */
  get isApp(): boolean {
    return (
      this.platform.is('cordova') ||
      (isServeMode() && !!sessionStorage.getItem('MOCK_APP'))
    );
  }

  /**
   * @description Indicator whether the app is running as an iOS App
   */
  get isIOSApp(): boolean {
    return this.platform.is('cordova') && this.platform.is('ios');
  }

  get accessType$(): Observable<AccessType> {
    return this._accessType.asObservable();
  }

  get isAccessTypeParticular() {
    if (!this.auth.authService.getState().isLogged) {
      return this._accessType.getValue() === 'particular';
    }
    return !!this.auth.authService.checkUserIsParticular();
  }

  get isAccessTypeBusiness() {
    if (!this.auth.authService.getState().isLogged) {
      return this._accessType.getValue() === 'business';
    }
    return !!this.auth.authService.checkUserIsCompany();
  }

  assertCordova() {
    if (this.platform.is('cordova') || (isServeMode() && !!sessionStorage.getItem('MOCK_APP'))) { 
      return Promise.resolve(); }
    return Promise.reject('Cordova not available');
  }

  isNativePlatform() {
    return this.platform.is('cordova');
  }

  updateAccessType(access: AccessType) {
    this._accessType.next(access);
  }

  /**
   * @description subscribe on page changes
   * @returns url of new page
   */
  onPageChange$(): Observable<string> {
    if (!this._onPageChange$) {
      this._onPageChange$ = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(
          (navigation: NavigationEnd) =>
            navigation.urlAfterRedirects || navigation.url
        ),
        tap(url => {
          this.previousPage = this.currentPage;
          this.currentPage = url;
        }),
        share()
      );
    }

    return this._onPageChange$;
  }

  /**
   * @description Indicates if the screen size corresponds to a mobile or tablet
   */
  get isMobileOrTablet(): boolean {
    return this.platform.width() < 960;
  }

  /**
   * @description Indicates if the screen size corresponds to a tablet
   */
  get isTablet(): boolean {
    return this.platform.width() > 568;
  }

  /**
   * @description Indicates if the screen size corresponds to a desktop
   */
  get isDesktop(): boolean {
    return this.platform.width() > 960;
  }

  get isDesktopDevice(): boolean {
    return this.platform.is('desktop');
  }
}
